import { ReactNode } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

interface ChartCardProps {
  title: string;
  /** One line under the title saying what the chart measures. */
  subtitle?: string;
  /** Rendered on the title row - a legend, a toggle, a total. */
  action?: ReactNode;
  /** Plot height in px, excluding the header. */
  height?: number;
  /**
   * True once a refetch is in flight. The previous render is held at reduced
   * opacity rather than replaced by a skeleton, so the card never jumps.
   */
  loading?: boolean;
  /** When true the plot is replaced by `emptyMessage` - no empty axes. */
  isEmpty?: boolean;
  emptyMessage?: string;
  /** Optional: a card rendering its empty state has no plot to wrap. */
  children?: ReactNode;
}

/**
 * The shell every chart in the app sits in: a card, a title, an optional
 * subtitle, and one consistent way of showing "nothing to plot" and "reloading".
 * Keeps the charts visually uniform without each page re-inventing the frame.
 */
export default function ChartCard({
  title,
  subtitle,
  action,
  height = 280,
  loading = false,
  isEmpty = false,
  emptyMessage,
  children,
}: ChartCardProps) {
  const { t } = useTranslation();

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent
        sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Box>

        {isEmpty ? (
          <Box
            sx={{
              height,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {emptyMessage || t("No data available")}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              // Sized to include the axis band, so the labels are never cut off
              // into a nested scrollbar.
              height,
              width: "100%",
              minWidth: 0,
              opacity: loading ? 0.45 : 1,
              transition: "opacity 0.2s ease-in-out",
            }}
          >
            {children}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
